import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobeIcon, CheckIcon } from 'lucide-react';
import { api } from '@/services/api';
import { useAuthStore } from '@/features/auth';
import toast from 'react-hot-toast';
import { useRTL } from '../hooks/useRTL';

interface Language {
  code: 'he' | 'en';
  name: string;
  nativeName: string;
  dir: 'rtl' | 'ltr';
}

const languages: Language[] = [
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', dir: 'rtl' },
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
];

/**
 * LanguageSwitcher Component
 * Globe icon with dropdown for switching languages (Hebrew/English)
 * Persists selection to database and overrides system defaults
 */
export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { user } = useAuthStore();
  const isRTL = useRTL();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Load user's saved language preference on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (!user) return; // Only fetch if user is logged in

      try {
        const { data } = await api.get('/users/me/language');
        const savedLanguage = data?.data?.preferredLanguage;

        // If user has a saved preference and it's different from current
        if (savedLanguage && savedLanguage !== i18n.language) {
          console.log(`Loading saved language preference: ${savedLanguage}`);
          await i18n.changeLanguage(savedLanguage);

          // Update document direction
          const lang = languages.find((l) => l.code === savedLanguage);
          if (lang) {
            document.documentElement.dir = lang.dir;
            document.documentElement.lang = lang.code;
          }
        }
      } catch (error) {
        console.error('Failed to load user language preference:', error);
        // Don't show error toast - this is a silent operation
      }
    };

    loadUserLanguage();
  }, [user, i18n]); // Re-run when user logs in/out

  // Handle language change
  const handleLanguageChange = async (language: Language) => {
    if (language.code === currentLanguage.code) {
      setIsOpen(false);
      return;
    }

    setIsLoading(true);

    try {
      // Update language in i18n
      await i18n.changeLanguage(language.code);

      // Update document direction
      document.documentElement.dir = language.dir;
      document.documentElement.lang = language.code;

      // Persist to database if user is logged in
      if (user) {
        await api.patch('/users/me/language', { preferredLanguage: language.code });
        toast.success(
          language.code === 'he'
            ? 'שפה עודכנה בהצלחה'
            : 'Language updated successfully'
        );
      }

      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
      toast.error(
        currentLanguage.code === 'he'
          ? 'שגיאה בעדכון שפה'
          : 'Failed to update language'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Arcade animation keyframes */}
      <style>{`
        .arcade-language-button {
          transition: all 0.15s ease;
        }

        .arcade-language-button:hover {
          transform: translateY(-2px);
        }

        .arcade-language-button:active {
          transform: translateY(2px);
          box-shadow: 2px 2px 0px var(--theme-border) !important;
        }

        .arcade-language-option {
          transition: all 0.1s ease;
        }

        .arcade-language-option:hover {
          transform: translateX(4px);
        }

        .arcade-language-option:active {
          transform: translateX(2px);
        }
      `}</style>

      <div className="relative" ref={dropdownRef}>
        {/* Globe Icon Button - Circular Frame */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className="typo-navigation arcade-language-button relative disabled:opacity-50 flex items-center justify-center"
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: 'var(--theme-surface)',
            color: 'var(--theme-text)',
            border: '3px solid var(--theme-border)',
            boxShadow: '3px 3px 0px var(--theme-border)',
            fontFamily: 'var(--font-pixel), monospace',
          }}
          aria-label="Change language"
        >
          <GlobeIcon className="h-6 w-6" />
        </button>

      {/* Arcade Dropdown */}
      {isOpen && (
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 min-w-max z-50`}
          style={{
            backgroundColor: 'var(--theme-card-yellow)',
            border: '4px solid var(--theme-border)',
            boxShadow: '8px 8px 0px var(--theme-border)',
          }}
        >
          {/* Arcade pixel corner decorations */}
          <div
            className="absolute top-0 left-0 w-2 h-2"
            style={{ backgroundColor: 'var(--theme-border)' }}
          />
          <div
            className="absolute top-0 right-0 w-2 h-2"
            style={{ backgroundColor: 'var(--theme-border)' }}
          />
          <div
            className="absolute bottom-0 left-0 w-2 h-2"
            style={{ backgroundColor: 'var(--theme-border)' }}
          />
          <div
            className="absolute bottom-0 right-0 w-2 h-2"
            style={{ backgroundColor: 'var(--theme-border)' }}
          />

          {/* Arcade Header */}
          <div
            className="p-3"
            style={{
              backgroundColor: 'var(--theme-card-cyan)',
              borderBottom: '3px solid var(--theme-border)',
            }}
          >
            <h3
              className="text-sm font-bold uppercase"
              style={{
                color: 'var(--theme-text)',
                fontFamily: 'var(--font-pixel), monospace',
                textShadow: '2px 2px 0px var(--theme-border)',
              }}
            >
              {currentLanguage.code === 'he' ? 'בחר שפה' : 'Select Language'}
            </h3>
          </div>

          {/* Arcade Language Options */}
          <div className="py-2 px-2">
            {languages.map((language) => {
              const isSelected = language.code === currentLanguage.code;

              return (
                <button
                  key={language.code}
                  type="button"
                  onClick={() => handleLanguageChange(language)}
                  disabled={isLoading}
                  className="arcade-language-option w-full flex items-center justify-between px-4 py-2.5 disabled:opacity-50 mb-2"
                  style={{
                    backgroundColor: isSelected ? 'var(--theme-card-purple)' : 'transparent',
                    border: isSelected ? '2px solid var(--theme-border)' : '2px solid transparent',
                    boxShadow: isSelected ? '3px 3px 0px var(--theme-border)' : 'none',
                    fontFamily: 'var(--font-pixel), monospace',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold uppercase" style={{ color: 'var(--theme-text)' }}>
                      {language.nativeName}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--theme-text-muted)' }}>
                      ({language.name})
                    </span>
                  </div>

                  {isSelected && (
                    <CheckIcon className="h-4 w-4" style={{ color: 'var(--theme-text)' }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Arcade Footer Note */}
          <div
            className="p-2 text-center"
            style={{
              borderTop: '3px solid var(--theme-border)',
              backgroundColor: 'var(--theme-card-cyan)',
            }}
          >
            <p
              className="text-[10px] font-bold uppercase"
              style={{
                color: 'var(--theme-text)',
                fontFamily: 'var(--font-pixel), monospace',
              }}
            >
              {currentLanguage.code === 'he'
                ? 'הבחירה נשמרת למשתמש'
                : 'Selection saved to profile'}
            </p>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
